import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../../services/auth';
import TimezoneSelector from '../TimezoneSelector/TimezoneSelector.jsx';
import AvatarUpload from '../AvatarUpload/AvatarUpload.jsx';
import './ProfileEditForm.css';

const ProfileEditForm = ({ user, onSave, onCancel, onError }) => {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    language: 'en',
    timezone: 'UTC'
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        language: user.language || 'en',
        timezone: user.timezone || 'UTC'
      });
    }
  }, [user]);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  }, [errors]);

  // Handle timezone change
  const handleTimezoneChange = useCallback((timezone) => {
    setFormData(prev => ({
      ...prev,
      timezone
    }));
  }, []);

  // Handle password input changes
  const handlePasswordChange = useCallback((e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  }, [passwordErrors]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = t('profile:validation.username_required');
    } else if (formData.username.length < 2) {
      newErrors.username = t('profile:validation.username_min_length');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('profile:validation.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('profile:validation.email_invalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const updatedUser = await authService.updateProfile(formData);
      
      // Update i18n language if it changed
      if (formData.language !== user.language) {
        await i18n.changeLanguage(formData.language);
      }
      
      onSave(updatedUser);
    } catch (error) {
      if (error.response?.status === 409) {
        onError(t('profile:errors.username_email_exists'));
      } else {
        onError(error.response?.data?.message || t('profile:errors.update_failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Validate password form
  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword.trim()) {
      newErrors.currentPassword = t('profile:validation.current_password_required');
    }

    if (!passwordData.newPassword.trim()) {
      newErrors.newPassword = t('profile:validation.new_password_required');
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = t('profile:validation.new_password_min_length');
    }

    if (!passwordData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('profile:validation.confirm_password_required');
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t('profile:validation.passwords_must_match');
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle password change
  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setChangingPassword(true);
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      
      // Clear password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordSection(false);
      
      // Show success message
      onError(`✓ ${t('profile:security.password_changed')}`, 'success');
    } catch (error) {
      if (error.response?.data?.message?.includes('Current password is incorrect')) {
        onError(t('profile:errors.current_password_incorrect'));
      } else {
        onError(error.response?.data?.message || t('profile:errors.password_change_failed'));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle avatar update
  const handleAvatarUpdate = useCallback((updatedUser) => {
    onSave(updatedUser);
  }, [onSave]);

  return (
    <div className="profile-edit-form">
      <form onSubmit={handleSubmit} className="edit-form">
        {/* Avatar Upload Section */}
        <div className="form-section">
          <h3 className="section-title">{t('profile:avatar.title')}</h3>
          <AvatarUpload
            user={user}
            onAvatarUpdate={handleAvatarUpdate}
            onError={onError}
          />
        </div>

        {/* Basic Information */}
        <div className="form-section">
          <h3 className="section-title">{t('profile:basic_info.title')}</h3>
          
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              {t('common:labels.username')} *
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={saving}
              className={`form-input ${errors.username ? 'error' : ''}`}
              autoComplete="username"
            />
            {errors.username && (
              <span className="error-message">{errors.username}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('common:labels.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={saving}
              className={`form-input ${errors.email ? 'error' : ''}`}
              autoComplete="email"
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="role" className="form-label">
              {t('common:labels.role')}
            </label>
            <input
              type="text"
              id="role"
              value={user?.role || ''}
              disabled
              className="form-input disabled"
              title={t('profile:role_readonly_tooltip')}
            />
            <small className="help-text">
              {t('profile:role_readonly_help')}
            </small>
          </div>
        </div>

        {/* Preferences */}
        <div className="form-section">
          <h3 className="section-title">{t('profile:preferences.title')}</h3>
          
          <div className="form-group">
            <label htmlFor="language" className="form-label">
              {t('common:labels.language')}
            </label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleChange}
              disabled={saving}
              className="form-select"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>

          <div className="form-group">
            <TimezoneSelector
              value={formData.timezone}
              onChange={handleTimezoneChange}
              disabled={saving}
            />
          </div>
        </div>

        {/* Security Section */}
        <div className="form-section">
          <h3 className="section-title">{t('profile:security.title')}</h3>
          
          <div className="form-group">
            <button
              type="button"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              className="btn btn-secondary"
              disabled={saving || changingPassword}
            >
              {t('profile:security.change_password')}
            </button>
          </div>

          {showPasswordSection && (
            <div className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword" className="form-label">
                  {t('profile:security.current_password')} *
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  disabled={changingPassword}
                  className={`form-input ${passwordErrors.currentPassword ? 'error' : ''}`}
                  autoComplete="current-password"
                />
                {passwordErrors.currentPassword && (
                  <span className="error-message">{passwordErrors.currentPassword}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="newPassword" className="form-label">
                  {t('profile:security.new_password')} *
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  disabled={changingPassword}
                  className={`form-input ${passwordErrors.newPassword ? 'error' : ''}`}
                  autoComplete="new-password"
                />
                {passwordErrors.newPassword && (
                  <span className="error-message">{passwordErrors.newPassword}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  {t('profile:security.confirm_password')} *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  disabled={changingPassword}
                  className={`form-input ${passwordErrors.confirmPassword ? 'error' : ''}`}
                  autoComplete="new-password"
                />
                {passwordErrors.confirmPassword && (
                  <span className="error-message">{passwordErrors.confirmPassword}</span>
                )}
              </div>

              <div className="password-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordSection(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordErrors({});
                  }}
                  disabled={changingPassword}
                  className="btn btn-secondary"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handlePasswordSubmit}
                  disabled={changingPassword}
                  className="btn btn-primary"
                >
                  {changingPassword ? t('profile:saving') : t('common:buttons.save')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="btn btn-secondary"
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? t('profile:saving') : t('common:buttons.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileEditForm;